import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import {AlertTriangle, BellRing, Moon, Send, ShieldCheck, Truck} from "lucide-react";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast} from "../../utils/utility";
import {getRole, ROLES} from "../../utils/rolePermissions";
import {disableWebPush, enableWebPush, isWebPushOnHere, setVehicleEnabled, webPushState} from "../../utils/webPush";
import Card, {CardHeader} from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Spinner from "../../components/ui/Spinner";
import {cn} from "../../utils/cn";

/**
 * Profile → Vehicle alerts. One person's GPS-tracker alert settings (GET/PUT api/v1/push/settings —
 * the same ones the Android app's Profile → Vehicle alerts edits) plus whether this browser gets
 * them. Every change saves at once. Security alerts have no off switch, only a per-vehicle mute.
 */
const settingsUrl = () => `${Constants.BASE_URL}${Constants.API_V1}/push/settings`;
const auth = () => ({headers: {Authorization: `${Constants.BEARER} ${getAuthToken()}`}});

async function fetchTrackers() {
  const clientId = getClientIdOfUser();
  const role = getRole();
  if (role === ROLES.CLIENT_ADMIN || role === ROLES.CLIENT_MANAGER || role === ROLES.SUPER_ADMIN) {
    const res = await axios.get(
      `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/assets/getAllAssets`,
      {...auth(), params: {assetType: "GT06_TRACKER"}}
    );
    return (res.data?.responseData || []).map((a) => ({id: a.assetUnitId, name: a.name}));
  }
  const res = await axios.get(
    `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/asset-management/user/${getLoggedInUserId()}`,
    auth()
  );
  return (Array.isArray(res.data) ? res.data : [])
    .filter((a) => !a.returnDate && a.asset?.assetType === "GT06_TRACKER")
    .map((a) => ({id: a.asset.assetUnitId, name: a.asset.name}));
}

const BROWSER_COPY = {
  unsupported: "This browser can't show alerts. Use Chrome, Edge or Firefox, or the Yekkada Android app.",
  unconfigured: "Browser alerts aren't set up for this dashboard yet. The Yekkada Android app gets them.",
  denied: "Notifications are blocked for this site. Allow them in the browser's site settings, then reload.",
};

export default function VehicleAlerts() {
  const [settings, setSettings] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [browserOn, setBrowserOn] = useState(isWebPushOnHere());
  const [browserState, setBrowserState] = useState(webPushState());
  const [busy, setBusy] = useState(false);
  const saving = useRef(Promise.resolve());

  useEffect(() => {
    axios.get(settingsUrl(), auth())
      .then((res) => setSettings(res.data))
      .catch(() => showToast("Couldn't load your alert settings", {type: "error"}));
    fetchTrackers().then(setVehicles).catch(() => setVehicles([]));
  }, []);

  // Saves are chained so two quick toggles can't land out of order.
  const save = useCallback((next) => {
    setSettings(next);
    saving.current = saving.current
      .then(() => axios.put(settingsUrl(), next, auth()))
      .catch(() => showToast("Couldn't save. Check your connection and try again.", {type: "error"}));
  }, []);

  const set = (field, value) => save({...settings, [field]: value});

  // Debugging aid: the server pushes one notification straight to every phone/browser this person
  // registered, skipping all alert rules. Its `result` says what happened (also on 404/502/503).
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${Constants.BASE_URL}${Constants.API_V1}/push/test`, {}, auth());
      showToast(res.data?.result || "Test sent", {type: "success"});
    } catch (e) {
      showToast(e?.response?.data?.result || e?.message || "Couldn't send the test", {type: "error"});
    } finally {
      setTesting(false);
    }
  };

  const toggleBrowser = async () => {
    setBusy(true);
    try {
      if (browserOn) {
        await disableWebPush();
        setBrowserOn(false);
      } else {
        const result = await enableWebPush();
        setBrowserState(webPushState());
        setBrowserOn(result === "granted");
        if (result === "granted") showToast("Alerts are on in this browser", {type: "success"});
      }
    } catch (e) {
      showToast(e?.message || "Couldn't turn on browser alerts", {type: "error"});
    } finally {
      setBusy(false);
    }
  };

  if (getRole() === ROLES.MOBI_TRACK) return null;
  if (!settings) return <div className="p-8 flex justify-center"><Spinner /></div>;

  const muted = new Set(settings.mutedDeviceIds || []);
  const browserBlocked = BROWSER_COPY[browserState];

  return (
    <div className="space-y-4 max-w-3xl">
      {!settings.companyPushEnabled && (
        <Banner>
          App alerts are switched off for your company. An admin can turn on <b>Push</b> in
          Settings → Notifications. Your choices below are kept for when it's on.
        </Banner>
      )}

      <Card>
        <CardHeader
          title="This browser"
          subtitle="Get vehicle alerts on this computer, even when the dashboard isn't open."
          action={!browserBlocked && (
            <Button variant={browserOn ? "secondary" : "primary"} loading={busy} onClick={toggleBrowser} icon={BellRing}>
              {browserOn ? "Turn off" : "Turn on"}
            </Button>
          )}
        />
        {browserBlocked && <p className="px-5 py-4 text-sm text-ink-600">{browserBlocked}</p>}
        <div className="px-5 py-3 border-t border-ink-100 flex items-center justify-between gap-3">
          <p className="text-xs text-ink-500">
            Not getting alerts? Send a test to your phone and this browser — it skips quiet hours and mutes.
          </p>
          <Button variant="secondary" size="sm" icon={Send} loading={testing} onClick={sendTest}>
            Send test notification
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Alert types" subtitle="Applies to your phone and this browser." />
        <div className="divide-y divide-ink-100">
          <Row icon={ShieldCheck} title="Security" subtitle="Power cut, tamper, battery critical. Always on — mute a vehicle below if needed.">
            <Switch checked disabled label="Security alerts" />
          </Row>
          <Row title="Overspeed" subtitle="At most one alert per vehicle every 15 minutes">
            <Switch checked={settings.overspeedEnabled} onChange={(v) => set("overspeedEnabled", v)} label="Overspeed" />
          </Row>
          <Row title="Engine on / off" subtitle="Quick on-off flicks are ignored; one alert per vehicle replaces the last">
            <Switch checked={settings.ignitionEnabled} onChange={(v) => set("ignitionEnabled", v)} label="Engine on / off" />
          </Row>
          <Row title="Trip summary" subtitle="Distance and top speed when a vehicle stops">
            <Switch
              checked={settings.tripSummaryEnabled}
              disabled={!settings.ignitionEnabled}
              onChange={(v) => set("tripSummaryEnabled", v)}
              label="Trip summary"
            />
          </Row>
        </div>
      </Card>

      <Card>
        <CardHeader title="Quiet hours" subtitle="No engine or speed alerts in this window. Security alerts still come through." />
        <div className="divide-y divide-ink-100">
          <Row icon={Moon} title="Quiet hours" subtitle="Uses the time zone of your phone or this browser">
            <Switch checked={settings.quietHoursEnabled} onChange={(v) => set("quietHoursEnabled", v)} label="Quiet hours" />
          </Row>
          <Row title="From – to">
            <div className="flex items-center gap-2">
              <TimeInput value={settings.quietStart} disabled={!settings.quietHoursEnabled} onChange={(v) => set("quietStart", v)} label="Quiet hours start" />
              <span className="text-ink-400">–</span>
              <TimeInput value={settings.quietEnd} disabled={!settings.quietHoursEnabled} onChange={(v) => set("quietEnd", v)} label="Quiet hours end" />
            </div>
          </Row>
          <Row title="Starts during quiet hours" subtitle="Alert loudly if an engine starts in quiet hours (possible unauthorised use)">
            <Switch
              checked={settings.afterHoursAlert}
              disabled={!settings.quietHoursEnabled}
              onChange={(v) => set("afterHoursAlert", v)}
              label="Starts during quiet hours"
            />
          </Row>
        </div>
      </Card>

      <Card>
        <CardHeader title="Vehicles" subtitle="Turn a vehicle off to mute all of its alerts, including security." />
        {vehicles === null ? (
          <div className="p-6 flex justify-center"><Spinner /></div>
        ) : vehicles.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-500">No GPS trackers in your fleet yet.</p>
        ) : (
          <div className="divide-y divide-ink-100">
            {[...vehicles].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)).map((v) => (
              <Row key={v.id} icon={Truck} title={v.name || v.id} subtitle={muted.has(v.id) ? "Muted" : "All alerts"}>
                <Switch
                  checked={!muted.has(v.id)}
                  onChange={(on) => set("mutedDeviceIds", setVehicleEnabled(settings.mutedDeviceIds, v.id, on))}
                  label={`Alerts for ${v.name || v.id}`}
                />
              </Row>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Banner({children}) {
  return (
    <div className="flex gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-ink-800">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning-600" aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

function Row({icon: Icon, title, subtitle, children}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3 min-h-[48px]">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink-900">{title}</p>
        {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Switch({checked, onChange, disabled, label}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange && onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-fast",
        checked ? "bg-brand-600" : "bg-ink-300",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-fast",
          checked ? "translate-x-5" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

function TimeInput({value, onChange, disabled, label}) {
  return (
    <input
      type="time"
      aria-label={label}
      value={(value || "").substring(0, 5)}
      disabled={disabled}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      className="h-9 rounded-md border border-ink-300 px-2 text-sm disabled:opacity-50"
    />
  );
}
